/**
 * Resumable multipart bookkeeping.
 *
 * A browser tab is closed, an MV3 service worker is killed at Chrome's
 * discretion, a laptop sleeps. A single 400 MB PUT that dies at 380 MB starts
 * again from zero. Parts are checkpointed in IndexedDB after each one lands,
 * so the next attempt resumes at the first missing part.
 *
 * The plan itself is pure arithmetic and lives here so it can be tested
 * without a browser, an S3 bucket or a service worker.
 */

export interface PartPlan {
  /** S3 part numbers are 1-based. */
  partNumber: number;
  start: number;
  /** Exclusive. */
  end: number;
  size: number;
}

/** S3 requires every part but the last to be at least 5 MB. */
export const MIN_PART_BYTES = 5 * 1024 * 1024;

/** S3 caps a multipart upload at 10 000 parts. */
export const MAX_PARTS = 10_000;

/** Below this the round trip of init/parts/complete costs more than it saves. */
export const MULTIPART_THRESHOLD_BYTES = 16 * 1024 * 1024;

/** Big enough that a 2 GB object stays well inside MAX_PARTS. */
export const DEFAULT_PART_BYTES = 16 * 1024 * 1024;

export function choosePartSize(totalBytes: number, preferred = DEFAULT_PART_BYTES): number {
  const size = Math.max(MIN_PART_BYTES, preferred);
  if (totalBytes <= size) return size;
  const needed = Math.ceil(totalBytes / MAX_PARTS);
  return Math.max(size, Math.ceil(needed / MIN_PART_BYTES) * MIN_PART_BYTES);
}

export function planParts(totalBytes: number, partSizeBytes: number): PartPlan[] {
  if (totalBytes < 0) throw new Error(`totalBytes must be >= 0, got ${totalBytes}`);
  if (partSizeBytes <= 0) throw new Error(`partSizeBytes must be > 0, got ${partSizeBytes}`);
  const parts: PartPlan[] = [];
  let start = 0;
  let partNumber = 1;
  while (start < totalBytes) {
    const end = Math.min(start + partSizeBytes, totalBytes);
    parts.push({ partNumber, start, end, size: end - start });
    start = end;
    partNumber++;
  }
  return parts;
}

/** partNumber -> ETag returned by S3. The resume checkpoint. */
export type CompletedParts = Record<number, string>;

export function pendingParts(plan: PartPlan[], completed: CompletedParts): PartPlan[] {
  return plan.filter((p) => !completed[p.partNumber]);
}

export function completedBytes(plan: PartPlan[], completed: CompletedParts): number {
  return plan.reduce((n, p) => (completed[p.partNumber] ? n + p.size : n), 0);
}

/** 0..1 by BYTES, not by part count — parts are uneven at the tail. */
export function partsProgress(plan: PartPlan[], completed: CompletedParts): number {
  const total = plan.reduce((n, p) => n + p.size, 0);
  if (total === 0) return 1;
  return completedBytes(plan, completed) / total;
}

export function isComplete(plan: PartPlan[], completed: CompletedParts): boolean {
  return plan.length > 0 && pendingParts(plan, completed).length === 0;
}

export interface CompletedPart {
  partNumber: number;
  etag: string;
}

/** Every part with its ETag, ordered by part number: what the server completes the upload with. */
export function completionManifest(plan: PartPlan[], completed: CompletedParts): CompletedPart[] {
  const missing = pendingParts(plan, completed);
  if (missing.length > 0) {
    throw new Error(`cannot complete: ${missing.length} part(s) still pending`);
  }
  return plan.map((p) => ({ partNumber: p.partNumber, etag: completed[p.partNumber] }));
}

/**
 * Persisted between attempts. Keyed by what is being uploaded (a recording, a
 * file): it has at most one upload in flight, and a second attempt should join
 * the first rather than start a rival multipart upload the server would abort.
 */
export interface UploadCheckpoint {
  key: string;
  /** The server-side object the upload belongs to (a capture id, a source id). */
  ownerId: string;
  uploadId: string;
  /** Bytes of the body as PUT (compressed, if it was) — the plan must match. */
  totalBytes: number;
  partSizeBytes: number;
  completed: CompletedParts;
  updatedAt: number;
}

/**
 * A checkpoint is only usable if it describes the same bytes. A recording
 * re-serialized after an edit compresses to a different length, and resuming
 * onto it would splice two different objects together.
 */
export function checkpointMatches(
  checkpoint: UploadCheckpoint | null,
  ownerId: string,
  totalBytes: number,
): checkpoint is UploadCheckpoint {
  if (!checkpoint) return false;
  return checkpoint.ownerId === ownerId && checkpoint.totalBytes === totalBytes;
}
